import { Set as ImmutableSet } from 'immutable';
import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';

import { getSpendingAccounts } from 'app-components/flux/accounts/accountsSelectors';
import { getConversationalCategoryData } from 'app-components/flux/conversationalReports/';
import { mkFilterObject } from 'app-components/flux/filters/filtersTypes';
import { getLogger } from 'app-components/utils/core';
import { constructPredefinedInterval } from 'app-components/utils/date';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { AI_CHAT_REDUCER_KEY } from '../../aiChatTypes';
import { safeParse } from '../../aiChatUtils';
import { cacheQueryResult } from '../../aiQueryCacheSlice';
import { returnToolResponse } from '../llmToolsFramework';
import { DATE_RANGES, type ToolMessage } from '../llmToolsTypes';

const log = getLogger('llmSpendingTools');

type SpendingCategoryType = 'spending' | 'reimbursement';

type SpendingCategoryRow = {
  category: string;
  amountSigned: number;
  amountAbs: number;
  type: SpendingCategoryType;
};

type SpendingCategorySummary = {
  categoryCount: number;
  grossSpending: number;
  reimbursements: number;
  netSpending: number;
  topSpendingCategory: string | null;
  topSpendingAmount: number | null;
  topReimbursementCategory: string | null;
  topReimbursementAmount: number | null;
};

function normalizeSpendingCategoryRows(rows: Array<{ category: string; periodAmount: string }>): SpendingCategoryRow[] {
  return rows
    .map((item) => {
      const amountSigned = parseFloat(item.periodAmount);
      if (!Number.isFinite(amountSigned) || amountSigned === 0) return null;

      return {
        category: item.category,
        amountSigned,
        amountAbs: Math.abs(amountSigned),
        type: amountSigned < 0 ? 'spending' : 'reimbursement',
      } satisfies SpendingCategoryRow;
    })
    .filter((item): item is SpendingCategoryRow => item != null)
    .sort((a, b) => b.amountAbs - a.amountAbs);
}

function buildSpendingCategorySummary(rows: SpendingCategoryRow[]): SpendingCategorySummary {
  const spendingRows = rows.filter((row) => row.type === 'spending');
  const reimbursementRows = rows.filter((row) => row.type === 'reimbursement');
  const grossSpending = spendingRows.reduce((sum, row) => sum + row.amountAbs, 0);
  const reimbursements = reimbursementRows.reduce((sum, row) => sum + row.amountAbs, 0);
  const topSpending = spendingRows[0];
  const topReimbursement = reimbursementRows[0];

  return {
    categoryCount: rows.length,
    grossSpending: parseFloat(grossSpending.toFixed(2)),
    reimbursements: parseFloat(reimbursements.toFixed(2)),
    netSpending: parseFloat((grossSpending - reimbursements).toFixed(2)),
    topSpendingCategory: topSpending?.category ?? null,
    topSpendingAmount: topSpending?.amountAbs ?? null,
    topReimbursementCategory: topReimbursement?.category ?? null,
    topReimbursementAmount: topReimbursement?.amountAbs ?? null,
  };
}

export const spendingTools = {
  get_category_data: {
    definition: {
      name: 'get_category_data',
      description: `Retrieve spending by category, and optionally, transaction data, for the user to help answer questions about their spending. It takes up to 4 parameters, a pre-defined Date Range, an optional custom start and ending date, and a request for transactions. Constrain the date range to only what you need for the given request. The pre-defined date range must be one of the following: ${DATE_RANGES}. If you specify CUSTOM, you must also provide 2 optional parameters, which is the startDate and endDate in YYYY-MM-DD format. NEVER show id, coa, or categoryId fields to the user - these are internal fields. Positive values in the spending breakdown represent reimbursements/offsets rather than additional spending and are returned separately in the normalized category output. Be concise and brief in your response. When this tool returns a resultId, call render_chart with that resultId and a chartType from availableRenderHints whenever a chart would help answer the question.`,
      parameters: {
        type: 'object' as const,
        properties: {
          preDefinedRange: {
            type: 'string' as const,
            description: `The date range to process. One of: ${DATE_RANGES}. If you specify CUSTOM, you must also provide 2 optional parameters, which is the startDate and endDate in YYYY-MM-DD format.`,
          },
          startDate: {
            type: 'string' as const,
            description: 'The start date in YYYY-MM-DD format. Required if preDefinedRange is CUSTOM.',
          },
          endDate: {
            type: 'string' as const,
            description: 'The end date in YYYY-MM-DD format. Required if preDefinedRange is CUSTOM.',
          },
          requestTransactions: {
            type: 'boolean' as const,
            description:
              'ONLY SET TO TRUE IF NECESSARY.  If true, the tool will return transaction data as well. Default is false.',
          },
        },
        required: ['preDefinedRange', 'startDate', 'endDate', 'requestTransactions'],
      },
    },
    handler: (msg: ToolMessage) => {
      const fnArgs = safeParse(msg.arguments) || {};

      const state = store?.getState() as RootState | undefined;

      if (!state) {
        return returnToolResponse(msg, { success: false, error: 'Store not available.' });
      }

      const spendingAccounts = getSpendingAccounts(state);
      const filterObject = mkFilterObject({ accounts: ImmutableSet(spendingAccounts.keySeq()) });

      const startDate = DateTime.fromISO(fnArgs.startDate || '', { zone: 'utc' });
      const endDate = DateTime.fromISO(fnArgs.endDate || '', { zone: 'utc' });

      const preDefinedDateRange = constructPredefinedInterval(
        {
          value: fnArgs?.preDefinedRange || 'RECENT_12_MONTHS',
          startDate: startDate,
          endDate: endDate,
        },
        DateTime.local().startOf('day'), // this constantly changes as it is a DateTime, we need to lock it to the nearest day
      );

      log.debug(
        'llmTools/spending: CALLING Income and Spending data Selector',
        filterObject,
        fnArgs.preDefinedRange,
        preDefinedDateRange.toISODate(),
      );

      const categoryData = getConversationalCategoryData(state, {
        filterObject,
        preDefinedDateRange,
        includeTransactions: fnArgs.requestTransactions || false,
      });

      log.debug('llmTools/spending: ABOUT TO SEND income_and_spending_data RESPONSE', categoryData);

      const overallSpending = categoryData?.catJSON.overallSpending ?? [];
      const categories = normalizeSpendingCategoryRows(overallSpending);
      const categorySummary = buildSpendingCategorySummary(categories);

      let resultId: string | undefined;
      if (categories.length >= 2) {
        resultId = `qr_${uuid()}`;
        const conversationId = state[AI_CHAT_REDUCER_KEY]?.currentConversationId ?? 'session';
        const dateRange = fnArgs.preDefinedRange || 'RECENT_12_MONTHS';
        const queryKey = `${conversationId}:category_breakdown:${dateRange}${dateRange === 'CUSTOM' ? `:${fnArgs.startDate ?? ''}:${fnArgs.endDate ?? ''}` : ''}:category`;
        store?.dispatch(
          cacheQueryResult({
            resultId,
            conversationId,
            queryKind: 'category_breakdown',
            queryKey,
            fullData: categories,
            summary: {
              text: `Spending: gross $${categorySummary.grossSpending.toFixed(2)}, reimbursements $${categorySummary.reimbursements.toFixed(2)}, net $${categorySummary.netSpending.toFixed(2)}`,
              keyFacts: {
                ...categorySummary,
              },
            },
            dataShape: 'breakdown',
            cachedAt: Date.now(),
          }),
        );
      }

      return returnToolResponse(msg, {
        ...categoryData,
        categories,
        categorySummary,
        ...(resultId && { resultId, availableRenderHints: ['pie', 'bar', 'sidewaysBar'] }),
      });
    },
  },
};
